import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Eye, Activity, Clock, TrendingUp, Users, MousePointer, Calendar, Globe } from 'lucide-react';
import { quotesApi } from '../services/api';
import { useToast } from '../hooks/use-toast';

const QuoteTracking = ({ quoteId, quotesData, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchTrackingData = async () => {
    if (!quoteId) return;
    
    setLoading(true);
    try {
      const response = await quotesApi.getTracking(quoteId);
      setTrackingData(response.data);
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Tracking-Daten konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && quoteId) {
      fetchTrackingData();
    }
  }, [isOpen, quoteId]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'view': return <Eye className="w-4 h-4" />;
      case 'download': return <TrendingUp className="w-4 h-4" />;
      case 'accept': return <Activity className="w-4 h-4 text-green-600" />;
      case 'reject': return <Activity className="w-4 h-4 text-red-600" />;
      case 'negotiate': return <Activity className="w-4 h-4 text-orange-600" />;
      default: return <MousePointer className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'view': return 'bg-blue-100 text-blue-800';
      case 'download': return 'bg-purple-100 text-purple-800';
      case 'accept': return 'bg-green-100 text-green-800';
      case 'reject': return 'bg-red-100 text-red-800';
      case 'negotiate': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Quick stats from quote data (if available)
  const quickStats = quotesData ? {
    views: quotesData.views || 0,
    lastViewed: quotesData.lastViewed,
  } : null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {quickStats ? (
              <span className="text-xs">
                {quickStats.views > 0 ? `${quickStats.views}x` : '0x'}
              </span>
            ) : (
              'Tracking'
            )}
          </Button>
        )}
      </DialogTrigger>
      
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Angebots-Tracking & Analytics
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Lade Tracking-Daten...</p>
          </div>
        ) : trackingData ? (
          <div className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-600">Gesamtaufrufe</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {trackingData.total_views}
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-600">Eindeutige Besucher</span>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {trackingData.unique_views}
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-600">Erster Aufruf</span>
                </div>
                <div className="text-sm font-bold text-purple-600">
                  {formatDateTime(trackingData.first_viewed)}
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-600">Letzter Aufruf</span>
                </div>
                <div className="text-sm font-bold text-orange-600">
                  {formatDateTime(trackingData.last_viewed)}
                </div>
              </Card>
            </div>

            {/* Recent Activities */}
            {trackingData.activities && trackingData.activities.length > 0 && (
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Aktivitäten
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Aktivität</TableHead>
                      <TableHead>Zeitpunkt</TableHead>
                      <TableHead>IP-Adresse</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trackingData.activities.slice(-10).reverse().map((activity, index) => (
                      <TableRow key={activity.id || index}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getActivityIcon(activity.activity_type)}
                            <Badge className={getActivityColor(activity.activity_type)}>
                              {activity.activity_type === 'view' ? 'Angesehen' :
                               activity.activity_type === 'download' ? 'Heruntergeladen' :
                               activity.activity_type === 'accept' ? 'Angenommen' :
                               activity.activity_type === 'reject' ? 'Abgelehnt' :
                               activity.activity_type === 'negotiate' ? 'Verhandelt' :
                               activity.activity_type}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDateTime(activity.timestamp)}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {activity.ip_address}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {activity.details && Object.keys(activity.details).length > 0 
                            ? JSON.stringify(activity.details, null, 1).slice(0, 100) + '...'
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}

            {/* View History */}
            {trackingData.view_history && trackingData.view_history.length > 0 && (
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Aufrufe-Historie (Letzte 20)
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Zeitpunkt</TableHead>
                      <TableHead>IP-Adresse</TableHead>
                      <TableHead>Browser</TableHead>
                      <TableHead>Session</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trackingData.view_history.slice(-20).reverse().map((view, index) => (
                      <TableRow key={view.id || index}>
                        <TableCell className="text-sm">
                          {formatDateTime(view.timestamp)}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {view.ip_address}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                          {view.user_agent}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {view.session_id ? view.session_id.slice(0, 8) + '...' : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}

            {/* No Data Message */}
            {trackingData.total_views === 0 && (
              <Card className="p-8 text-center">
                <Eye className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2 text-gray-600">Noch keine Aufrufe</h3>
                <p className="text-gray-500">
                  Dieses Angebot wurde noch nicht vom Kunden aufgerufen.
                </p>
              </Card>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Keine Tracking-Daten verfügbar</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteTracking;